import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import VendorOnlyRoute from './components/VendorOnlyRoute';
import PermissionRoute from './components/PermissionRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import PharmacySetupRoute from './components/PharmacySetupRoute';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const DashboardLayout = lazy(() => import('./components/templates/DashboardLayout'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Statistics = lazy(() => import('./pages/Statistics'));
const Billing = lazy(() => import('./pages/Billing'));
const BillingCounter = lazy(() => import('./pages/BillingCounter'));
const BillingHistory = lazy(() => import('./pages/BillingHistory'));
const Prescriptions = lazy(() => import('./pages/Prescriptions'));
const Inventory = lazy(() => import('./pages/Inventory'));
const ProductMaster = lazy(() => import('./pages/ProductMaster'));
const AccountMaster = lazy(() => import('./pages/AccountMaster'));
const PurchaseBills = lazy(() => import('./pages/PurchaseBills'));
const LowStock = lazy(() => import('./pages/LowStock'));
const Expiry = lazy(() => import('./pages/Expiry'));
const LossManagement = lazy(() => import('./pages/LossManagement'));
const Payments = lazy(() => import('./pages/Payments'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const Transfers = lazy(() => import('./pages/Transfers'));
const PurchaseReturns = lazy(() => import('./pages/PurchaseReturns'));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'));
const PurchaseAnalytics = lazy(() => import('./pages/PurchaseAnalytics'));
const VendorRates = lazy(() => import('./pages/VendorRates'));
const PurchaseBudget = lazy(() => import('./pages/PurchaseBudget'));
const Gstr2b = lazy(() => import('./pages/Gstr2b'));
const GoodsReceipt = lazy(() => import('./pages/GoodsReceipt'));
const SupplierCreditNotes = lazy(() => import('./pages/SupplierCreditNotes'));
const Ledgers = lazy(() => import('./pages/Ledgers'));
const LedgerRedirect = lazy(() => import('./pages/LedgerRedirect'));
const PurchaseReports = lazy(() => import('./pages/PurchaseReports'));
const Alerts = lazy(() => import('./pages/Alerts'));
const GstPurchaseSummary = lazy(() => import('./pages/GstPurchaseSummary'));
const TaxMaster = lazy(() => import('./pages/TaxMaster'));
const Compliance = lazy(() => import('./pages/Compliance'));
const ManageAccess = lazy(() => import('./pages/ManageAccess'));
const PharmacySetup = lazy(() => import('./pages/PharmacySetup'));
const EmployeeMaster = lazy(() => import('./pages/EmployeeMaster'));
const StaffAdvances = lazy(() => import('./pages/StaffAdvances'));
const Payslips = lazy(() => import('./pages/Payslips'));
const Leaves = lazy(() => import('./pages/Leaves'));
const BankAccounts = lazy(() => import('./pages/BankAccounts'));
const BankingSummary = lazy(() => import('./pages/BankingSummary'));
const BankCheques = lazy(() => import('./pages/BankCheques'));
const CashBankLedger = lazy(() => import('./pages/CashBankLedger'));
const BankReceipts = lazy(() => import('./pages/BankReceipts'));
const BankPayments = lazy(() => import('./pages/BankPayments'));
const FundTransfer = lazy(() => import('./pages/FundTransfer'));
const DayBook = lazy(() => import('./pages/DayBook'));
const Reconciliation = lazy(() => import('./pages/Reconciliation'));

// Protected Route Components
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to={`/login${location.search || ''}`} replace />;
};

function AppRoutes() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      }
    >
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="statistics" element={<Statistics />} />
          <Route path="billing" element={<Billing />} />
          <Route path="billing-counter" element={<BillingCounter />} />
          <Route path="billing-history" element={<BillingHistory />} />
          <Route path="prescriptions" element={<Prescriptions />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="product-master" element={<ProductMaster />} />
          <Route path="account-master" element={<AccountMaster />} />
          <Route path="purchase-bills" element={<PurchaseBills />} />
          <Route path="purchase-orders" element={<VendorOnlyRoute><PurchaseOrders /></VendorOnlyRoute>} />
          <Route path="purchase-analytics" element={<VendorOnlyRoute><PurchaseAnalytics /></VendorOnlyRoute>} />
          <Route path="vendor-rates" element={<VendorOnlyRoute><VendorRates /></VendorOnlyRoute>} />
          <Route path="purchase-budget" element={<VendorOnlyRoute><PurchaseBudget /></VendorOnlyRoute>} />
          <Route path="gstr2b" element={<VendorOnlyRoute><Gstr2b /></VendorOnlyRoute>} />
          <Route path="goods-receipt" element={<VendorOnlyRoute><GoodsReceipt /></VendorOnlyRoute>} />
          <Route path="low-stock" element={<LowStock />} />
          <Route path="expiry" element={<Expiry />} />
          <Route path="loss-management" element={<LossManagement />} />
          <Route path="payments" element={<Payments />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<Settings />} />
          <Route
            path="pharmacy-setup"
            element={
              <PharmacySetupRoute>
                <PharmacySetup />
              </PharmacySetupRoute>
            }
          />
          <Route path="transfers" element={<Transfers />} />
          <Route path="purchase-returns" element={<PurchaseReturns />} />
          <Route path="supplier-credit-notes" element={<VendorOnlyRoute><SupplierCreditNotes /></VendorOnlyRoute>} />
          <Route path="ledgers" element={<VendorOnlyRoute><Ledgers /></VendorOnlyRoute>} />
          <Route path="vendor-ledger" element={<VendorOnlyRoute><LedgerRedirect side="vendors" /></VendorOnlyRoute>} />
          <Route path="customer-ledger" element={<VendorOnlyRoute><LedgerRedirect side="customers" /></VendorOnlyRoute>} />
          <Route path="purchase-reports" element={<VendorOnlyRoute><PurchaseReports /></VendorOnlyRoute>} />
          <Route path="alerts" element={<Alerts />} />
          <Route path="gst-purchase-summary" element={<VendorOnlyRoute><GstPurchaseSummary /></VendorOnlyRoute>} />
          <Route path="tax-master" element={<TaxMaster />} />
          <Route path="compliance" element={<Compliance />} />
          <Route path="employee-master" element={<PermissionRoute permission="staff_management"><EmployeeMaster /></PermissionRoute>} />
          <Route path="staff-advances" element={<PermissionRoute permission="staff_management"><StaffAdvances /></PermissionRoute>} />
          <Route path="payslips" element={<PermissionRoute permission="staff_management"><Payslips /></PermissionRoute>} />
          <Route
            path="manage-access"
            element={
              <PermissionRoute permission="staff_management">
                <SuperAdminRoute>
                  <ManageAccess />
                </SuperAdminRoute>
              </PermissionRoute>
            }
          />
          <Route path="leaves" element={<Leaves />} />
          <Route path="bank-accounts" element={<PermissionRoute permission="banking"><BankAccounts /></PermissionRoute>} />
          <Route path="banking-summary" element={<PermissionRoute permission="banking"><BankingSummary /></PermissionRoute>} />
          <Route path="bank-cheques" element={<PermissionRoute permission="banking"><BankCheques /></PermissionRoute>} />
          <Route path="cash-bank-ledger" element={<PermissionRoute permission="banking"><CashBankLedger /></PermissionRoute>} />
          <Route path="bank-receipts" element={<PermissionRoute permission="banking"><BankReceipts /></PermissionRoute>} />
          <Route path="bank-payments" element={<PermissionRoute permission="banking"><BankPayments /></PermissionRoute>} />
          <Route path="fund-transfer" element={<PermissionRoute permission="banking"><FundTransfer /></PermissionRoute>} />
          <Route path="day-book" element={<PermissionRoute permission="banking"><DayBook /></PermissionRoute>} />
          <Route path="reconciliation" element={<PermissionRoute permission="banking"><Reconciliation /></PermissionRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
